import { Form, Select } from "antd";
import { ReactElement, useEffect, useState } from "react";

import Label from "../../components/label/label.component";
import "./edit-financial.scss";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import instance from "../../store/axios";
import {
  ADD_TO_BREADCRUMB,
  LOADING,
  REMOVE_FROM_BREADCRUMB,
} from "../../store/actions";
import { AxiosResponse } from "axios";
import toast from "react-hot-toast";
import CollapseComponent from "../../components/collapse.component";
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import CustomInput from "src/components/custom-input/custom-input.component";
import TextArea from "antd/es/input/TextArea";
import { getStandingDataOptions } from "src/utils/helper/functions";

const EditFinancial: React.FC = (): ReactElement => {
  const { accountId, activeKyc } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isApproveCase = queryParams.get("approve");
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [submitForm, setSubmitForm] = useState<boolean>(false);

  const [accountDetails, setAccountDetails] = useState<any>();
  const [standingData, setStandingData] = useState<any>();

  useEffect(() => {
    async function prepareData() {
      dispatch({ type: LOADING, payload: true });
      await getStandindData();
      await getKycApi();
      dispatch({ type: LOADING, payload: false });
    }

    prepareData();

    dispatch({
      type: ADD_TO_BREADCRUMB,
      payload: {
        breadcrumb: [
          {
            text: `Update financial details`,
            type: "text",
          },
        ],
      },
    });

    return () => {
      dispatch({
        type: REMOVE_FROM_BREADCRUMB,
        payload: {
          breadcrumb: [
            {
              text: `Update financial details`,
            },
          ],
        },
      });
    };
  }, []);

  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmitForm(true);
      },
      () => {
        setSubmitForm(false);
      }
    );
  }, [values]);

  const getKycApi = async (): Promise<void> => {
    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}?kycId=${activeKyc}`
      );
      setAccountDetails(response.data.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/financial/:${accountId}/:${activeKyc}/edit`
      );
    }
  };

  const getStandindData = async (): Promise<void> => {
    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/option/standing-data`
      );
      setStandingData(response.data.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/financial/:${accountId}/:${activeKyc}/edit`
      );
    }
  };

  const getSourceOfIncome = () => {
    const array = [];
    if (accountDetails?.accountInfo[0]?.sourceOfIncome1)
      array.push("sourceOfIncome1");
    if (accountDetails?.accountInfo[0]?.sourceOfIncome2)
      array.push("sourceOfIncome2");
    if (accountDetails?.accountInfo[0]?.sourceOfIncome3)
      array.push("sourceOfIncome3");
    if (accountDetails?.accountInfo[0]?.sourceOfIncome4)
      array.push("sourceOfIncome4");
    if (accountDetails?.accountInfo[0]?.sourceOfIncome5)
      array.push("sourceOfIncome5");
    if (accountDetails?.accountInfo[0]?.sourceOfIncome6)
      array.push("sourceOfIncome6");
    if (accountDetails?.accountInfo[0]?.sourceOfIncome7)
      array.push("sourceOfIncome7");
    return array;
  };

  const getSourceOfWealth = () => {
    const array = [];

    if (accountDetails?.accountInfo[0]?.sourceOfWealth1)
      array.push("sourceOfWealth1");
    if (accountDetails?.accountInfo[0]?.sourceOfWealth2)
      array.push("sourceOfWealth2");
    if (accountDetails?.accountInfo[0]?.sourceOfWealth3)
      array.push("sourceOfWealth3");
    if (accountDetails?.accountInfo[0]?.sourceOfWealth4)
      array.push("sourceOfWealth4");
    if (accountDetails?.accountInfo[0]?.sourceOfWealth5)
      array.push("sourceOfWealth5");
    if (accountDetails?.accountInfo[0]?.sourceOfWealth6)
      array.push("sourceOfWealth6");

    return array;
  };

  const onSubmit = async (values: any): Promise<void> => {
    dispatch({ type: LOADING, payload: true });
    values.branch = "KW";

    if (values.investmentObjectiveId?.includes("Capital Protection")) {
      values.investmentObjectiveCapitalProtection = true;
    } else {
      values.investmentObjectiveCapitalProtection = false;
    }

    if (values.investmentObjectiveId?.includes("Income")) {
      values.investmentObjectiveIncome = true;
    } else {
      values.investmentObjectiveIncome = false;
    }

    if (values.investmentObjectiveId?.includes("Capital Growth")) {
      values.investmentObjectiveCapitalGrowth = true;
    } else {
      values.investmentObjectiveCapitalGrowth = false;
    }

    if (values.investmentObjectiveId?.includes("Speculation")) {
      values.investmentObjectiveSpeculation = true;
    } else {
      values.investmentObjectiveSpeculation = false;
    }

    delete values.investmentObjectiveId;

    for (let i = 1; i <= 7; i++) {
      if (values.sourceOfIncomeInformation.includes(`sourceOfIncome${i}`)) {
        values[`sourceOfIncome${i}`] = true;
      } else {
        values[`sourceOfIncome${i}`] = false;
      }
    }

    delete values.sourceOfIncomeInformation;

    for (let i = 1; i <= 6; i++) {
      if (values.wealthInformation.includes(`sourceOfWealth${i}`)) {
        values[`sourceOfWealth${i}`] = true;
      } else {
        values[`sourceOfWealth${i}`] = false;
      }
    }

    delete values.wealthInformation;

    values.kycId = accountDetails.accountInfo[0]?.id;
    values.expectedAmountActivity = +values.expectedAmountActivity;
    values.initialInvestmentAmount = +values.initialInvestmentAmount;

    try {
      await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}/financial-info/update`,
        values
      );

      toast.success("Financial information updated successfully");
      navigate(
        `/home/business-account/${accountId}/${activeKyc}/details?tab=7${isApproveCase ? "&approve=1" : ""
        }`
      );
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/financial/:${accountId}/:${activeKyc}/edit${isApproveCase ? "?approve=1" : ""
        }`
      );
    }

    dispatch({ type: LOADING, payload: false });
  };

  const getInvestmentObjectiveValues = () => {
    const valuesArray = [];
    if (accountDetails.accountInfo[0]?.investmentObjectiveCapitalGrowth) {
      valuesArray.push("Capital Growth");
    }
    if (accountDetails.accountInfo[0]?.investmentObjectiveCapitalProtection) {
      valuesArray.push("Capital Protection");
    }
    if (accountDetails.accountInfo[0]?.investmentObjectiveIncome) {
      valuesArray.push("Income");
    }
    if (accountDetails.accountInfo[0]?.investmentObjectiveSpeculation) {
      valuesArray.push("Speculation");
    }

    return valuesArray;
  };

  return (
    <div className="edit-financial-container flex flex-col">
      <BreadcrumbComponent />
      {accountDetails?.id && (
        <Form
          onFinish={onSubmit}
          scrollToFirstError
          className="add-edit-form"
          initialValues={{
            investmentObjectiveId: getInvestmentObjectiveValues(),
            investmentTypeId: accountDetails.accountInfo[0]?.investmentTypeId,
            annualRevenueId: accountDetails.accountInfo[0]?.annualRevenueId,
            totalWealthId: accountDetails.accountInfo[0]?.totalWealthId,
            expectedAmountActivity:
              accountDetails.accountInfo[0]?.expectedAmountActivity,
            riskToleranceId: accountDetails.accountInfo[0]?.riskToleranceId,
            investmentExpId: accountDetails.accountInfo[0]?.investmentExpId,
            yearsInvestmentSecId:
              accountDetails.accountInfo[0]?.yearsInvestmentSecId,
            initialInvestmentAmount:
              +accountDetails.accountInfo[0]?.initialInvestmentAmount,
            yearsInvestmentExpId:
              accountDetails.accountInfo[0]?.yearsInvestmentExpId,
            hasInvestmentAdvisor:
              accountDetails.accountInfo[0]?.hasInvestmentAdvisor,
            sourceOfIncomeInformation: getSourceOfIncome(),
            wealthInformation: getSourceOfWealth(),
            sourceOfIncome1AmtId:
              accountDetails.accountInfo[0]?.sourceOfIncome1AmtId,
            sourceOfIncome2AmtId:
              accountDetails.accountInfo[0]?.sourceOfIncome2AmtId,
            sourceOfIncome3AmtId:
              accountDetails.accountInfo[0]?.sourceOfIncome3AmtId,
            sourceOfIncome4AmtId:
              accountDetails.accountInfo[0]?.sourceOfIncome4AmtId,
            sourceOfIncome5AmtId:
              accountDetails.accountInfo[0]?.sourceOfIncome5AmtId,
            sourceOfIncome6AmtId:
              accountDetails.accountInfo[0]?.sourceOfIncome6AmtId,
            sourceOfIncome7AmtId:
              accountDetails.accountInfo[0]?.sourceOfIncome7AmtId,
            sourceOfWealth1AmtId:
              accountDetails.accountInfo[0]?.sourceOfWealth1AmtId,
            sourceOfWealth2AmtId:
              accountDetails.accountInfo[0]?.sourceOfWealth2AmtId,
            sourceOfWealth3AmtId:
              accountDetails.accountInfo[0]?.sourceOfWealth3AmtId,
            sourceOfWealth4AmtId:
              accountDetails.accountInfo[0]?.sourceOfWealth4AmtId,
            sourceOfWealth5AmtId:
              accountDetails.accountInfo[0]?.sourceOfWealth5AmtId,
            sourceOfWealth6AmtId:
              accountDetails.accountInfo[0]?.sourceOfWealth6AmtId,
            sourceOfIncomeDetails:
              accountDetails?.accountInfo[0]?.sourceOfIncomeDetails,
            sourceOfWealthDetails:
              accountDetails?.accountInfo[0]?.sourceOfWealthDetails,
          }}
          form={form}
        >
          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-lg font-semibold">
                  Financial information
                </h3>
              </div>
            }
          >
            <div className="fields flex justify-space-between wrap">
              <div className="field flex column">
                <Label text="Investment objective" />
                <Form.Item
                  name="investmentObjectiveId"
                  rules={[
                    {
                      required: true,
                      message: "Please select the Investment objective!",
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select Investment objective"
                    options={standingData?.investmentObjective?.filter((item: any) => ['kw', 'gc'].includes(item.branch))?.map(
                      (item: any) => {
                        return {
                          value: item.description,
                          label: item.description,
                        };
                      }
                    )}

                  />
                </Form.Item>
              </div>

              <div className="field flex flex-col">
                <Label text="Initial investment amount (USD Equivalent)" />
                <Form.Item
                  name="initialInvestmentAmount"
                  rules={[
                    {
                      required: true,
                      message:
                        "Please enter Initial investment amount (USD Equivalent)!",
                    },
                  ]}
                >
                  <CustomInput placeholder="Enter Initial investment amount (USD Equivalent)" />
                </Form.Item>
              </div>

              <div className="field flex flex-col">
                <Label text="Expected account activity volume (USD Equivalent)" />
                <Form.Item
                  name="expectedAmountActivity"
                  rules={[
                    {
                      required: true,
                      message:
                        "Please enter Expected account activity volume (USD Equivalent)!",
                    },
                  ]}
                >
                  <CustomInput placeholder="Enter Expected account activity volume (USD Equivalent)" />
                </Form.Item>
              </div>

              <div className="field flex flex-col">
                <Label text="Preferred Investment Type" />
                <Form.Item
                  name="investmentTypeId"
                  rules={[
                    {
                      required: true,
                      message: "Please select the Preferred Investment Type!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Preferred Investment Type"

                    options={[
                      ...getStandingDataOptions(
                        standingData,
                        "investmentType",
                        ["kw", "gc"],
                        false
                      ),
                    ]}
                  />
                </Form.Item>
              </div>

              <div className="field flex column">
                <Label text="The Estimated Annual Income (USD Equivalent)" />
                <Form.Item
                  name="annualRevenueId"
                  rules={[
                    {
                      required: true,
                      message:
                        "Please select The Estimated Annual Income (USD Equivalent)!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select The Estimated Annual Income (USD Equivalent)"

                    options={[
                      ...getStandingDataOptions(
                        standingData,
                        "revenueRange",
                        ["kw", "gc"],
                        false
                      ),
                    ]}
                  />
                </Form.Item>
              </div>

              <div className="field flex column">
                <Label text="Total wealth Estimated account (USD Equivalent)" />
                <Form.Item
                  name="totalWealthId"
                  rules={[
                    {
                      required: true,
                      message:
                        "Please select Total wealth Estimated account (USD Equivalent)!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Total wealth Estimated account (USD Equivalent)"

                    options={[
                      ...getStandingDataOptions(
                        standingData,
                        "totalWealth",
                        ["kw", "gc"],
                        false
                      ),
                    ]}
                  />
                </Form.Item>
              </div>

              <div className="field flex column">
                <Label text="Risk Tolerance" />
                <Form.Item
                  name="riskToleranceId"
                  rules={[
                    {
                      required: true,
                      message: "Please select Risk Tolerance!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Risk Tolerance"

                    options={[
                      ...getStandingDataOptions(
                        standingData,
                        "riskTolerance",
                        ["kw", "gc"],
                        false
                      ),
                    ]}
                  />
                </Form.Item>
              </div>

              <div className="field flex column">
                <Label text="Investment Experience" />
                <Form.Item
                  name="investmentExpId"
                  rules={[
                    {
                      required: true,
                      message: "Please select Investment Experience!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Investment Experience"

                    options={[
                      ...getStandingDataOptions(
                        standingData,
                        "investmentExperience",
                        ["kw", "gc"],
                        false
                      ),
                    ]}
                  />
                </Form.Item>
              </div>

              <div className="field flex column">
                <Label text="Years of Experience in Investment" />
                <Form.Item
                  name="yearsInvestmentExpId"
                  rules={[
                    {
                      required: true,
                      message:
                        "Please select Years of Experience in Investment!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Years of Experience in Investment"

                    options={[
                      ...getStandingDataOptions(
                        standingData,
                        "investmentExperienceYears",
                        ["kw", "gc"],
                        false
                      ),
                    ]}
                  />
                </Form.Item>
              </div>

              <div className="field flex column">
                <Label text="Do you rely on an investment advisor" />
                <Form.Item
                  name="hasInvestmentAdvisor"
                  rules={[
                    {
                      required: true,
                      message:
                        "Please select Do you rely on an investment advisor!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Do you rely on an investment advisor"
                    options={[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" },
                    ]}
                  />
                </Form.Item>
              </div>

              <div className="field flex column">
                <Label text="Investment information" />
                <Form.Item
                  name="sourceOfIncomeInformation"
                  rules={[
                    {
                      required: true,
                      message: "Please select investment information!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Income Information"
                    mode="multiple"
                    maxTagCount="responsive"
                    options={[
                      {
                        value: `sourceOfIncome1`,
                        label:
                          "Total annual income from salary, financial incentives and allowances",
                      },
                      {
                        value: `sourceOfIncome2`,
                        label: "Total annual income from personal business",
                      },
                      {
                        value: `sourceOfIncome3`,
                        label: "Total annual pension income",
                      },
                      {
                        value: `sourceOfIncome4`,
                        label:
                          "Total annual cash dividends from investments in local / foreign securities",
                      },
                      {
                        value: `sourceOfIncome5`,
                        label:
                          "Total annual income from local / foreign real estate",
                      },
                      {
                        value: `sourceOfIncome6`,
                        label:
                          "Total annual income from contributions in local / foreign real estate funds and / or projects",
                      },
                      {
                        value: `sourceOfIncome7`,
                        label: "Total interest from bank deposits",
                      },
                    ]}
                  />
                </Form.Item>
              </div>

              {form
                .getFieldValue("sourceOfIncomeInformation")
                ?.includes("sourceOfIncome1") && (
                  <div className="field flex column">
                    <Label text="Total annual income from salary, financial incentives and allowances" />
                    <Form.Item
                      name="sourceOfIncome1AmtId"
                      rules={[
                        {
                          required: true,
                          message:
                            "Please select Total annual income from salary, financial incentives and allowances!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Total annual income from salary, financial incentives and allowances"

                        options={[
                          ...getStandingDataOptions(
                            standingData,
                            "sourceOfIncome",
                            ["kw", "gc"],
                            false
                          ),
                        ]}
                      />
                    </Form.Item>
                  </div>
                )}

              {form
                .getFieldValue("sourceOfIncomeInformation")
                ?.includes("sourceOfIncome2") && (
                  <div className="field flex column">
                    <Label text="Total annual income from personal business" />
                    <Form.Item
                      name="sourceOfIncome2AmtId"
                      rules={[
                        {
                          required: true,
                          message:
                            "Please select Total annual income from personal business!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Total annual income from personal business"

                        options={[
                          ...getStandingDataOptions(
                            standingData,
                            "sourceOfIncome",
                            ["kw", "gc"],
                            false
                          ),
                        ]}
                      />
                    </Form.Item>
                  </div>
                )}

              {form
                .getFieldValue("sourceOfIncomeInformation")
                ?.includes("sourceOfIncome3") && (
                  <div className="field flex column">
                    <Label text="Total annual pension income" />
                    <Form.Item
                      name="sourceOfIncome3AmtId"
                      rules={[
                        {
                          required: true,
                          message: "Please select Total annual pension income!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Total annual pension income"

                        options={[
                          ...getStandingDataOptions(
                            standingData,
                            "sourceOfIncome",
                            ["kw", "gc"],
                            false
                          ),
                        ]}
                      />
                    </Form.Item>
                  </div>
                )}

              {form
                .getFieldValue("sourceOfIncomeInformation")
                ?.includes("sourceOfIncome4") && (
                  <div className="field flex column">
                    <Label text="Total annual cash dividends from investments in local / foreign securities" />
                    <Form.Item
                      name="sourceOfIncome4AmtId"
                      rules={[
                        {
                          required: true,
                          message:
                            "Please select Total annual cash dividends from investments in local / foreign securities!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Total annual cash dividends from investments in local / foreign securities"

                        options={[
                          ...getStandingDataOptions(
                            standingData,
                            "sourceOfIncome",
                            ["kw", "gc"],
                            false
                          ),
                        ]}
                      />
                    </Form.Item>
                  </div>
                )}

              {form
                .getFieldValue("sourceOfIncomeInformation")
                ?.includes("sourceOfIncome5") && (
                  <div className="field flex column">
                    <Label text="Total annual income from local / foreign real estate" />
                    <Form.Item
                      name="sourceOfIncome5AmtId"
                      rules={[
                        {
                          required: true,
                          message:
                            "Please select Total annual income from local / foreign real estate!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Total annual income from local / foreign real estate"

                        options={[
                          ...getStandingDataOptions(
                            standingData,
                            "sourceOfIncome",
                            ["kw", "gc"],
                            false
                          ),
                        ]}
                      />
                    </Form.Item>
                  </div>
                )}

              {form
                .getFieldValue("sourceOfIncomeInformation")
                ?.includes("sourceOfIncome6") && (
                  <div className="field flex column">
                    <Label text="Total annual income from contributions in local / foreign real estate funds and / or projects" />
                    <Form.Item
                      name="sourceOfIncome6AmtId"
                      rules={[
                        {
                          required: true,
                          message:
                            "Please select Total annual income from contributions in local / foreign real estate funds and / or projects!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Total annual income from contributions in local / foreign real estate funds and / or projects"

                        options={[
                          ...getStandingDataOptions(
                            standingData,
                            "sourceOfIncome",
                            ["kw", "gc"],
                            false
                          ),
                        ]}
                      />
                    </Form.Item>
                  </div>
                )}

              {form
                .getFieldValue("sourceOfIncomeInformation")
                ?.includes("sourceOfIncome7") && (
                  <div className="field flex column">
                    <Label text="Total interest from bank deposits" />
                    <Form.Item
                      name="sourceOfIncome7AmtId"
                      rules={[
                        {
                          required: true,
                          message:
                            "Please select Total interest from bank deposits!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Total interest from bank deposits"

                        options={[
                          ...getStandingDataOptions(
                            standingData,
                            "sourceOfIncome",
                            ["kw", "gc"],
                            false
                          ),
                        ]}
                      />
                    </Form.Item>
                  </div>
                )}

              <div className="fields flex flex-col">
                <Label text="Additional Details on Source of Income" />
                <Form.Item name="sourceOfIncomeDetails">
                  <TextArea
                    rows={4}
                    placeholder="Enter Additional Details on Source of Income"
                  />
                </Form.Item>
              </div>

              <div className="field flex flex-col">
                <Label text="Wealth information" />
                <Form.Item
                  name="wealthInformation"
                  rules={[
                    {
                      required: true,
                      message: "Please select Wealth information!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Wealth information"
                    mode="multiple"
                    maxTagCount="responsive"
                    options={[
                      {
                        value: "sourceOfWealth1",
                        label: "Saving accounts with local / foreign banks",
                      },
                      {
                        value: "sourceOfWealth2",
                        label: "Credit facilities from local / foreign banks",
                      },
                      {
                        value: "sourceOfWealth3",
                        label: "Inheritance",
                      },
                      {
                        value: "sourceOfWealth4",
                        label: "Local / foreign real estate",
                      },
                      {
                        value: "sourceOfWealth5",
                        label:
                          "Investments in local / foreign real estate projects",
                      },
                      {
                        value: "sourceOfWealth6",
                        label: "Investments in local / foreign securities",
                      },
                    ]}
                  />
                </Form.Item>
              </div>

              {form
                .getFieldValue("wealthInformation")
                ?.includes("sourceOfWealth1") && (
                  <div className="field flex column">
                    <Label text="Saving accounts with local / foreign banks" />
                    <Form.Item
                      name="sourceOfWealth1AmtId"
                      rules={[
                        {
                          required: true,
                          message:
                            "Please select Saving accounts with local / foreign banks!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Saving accounts with local / foreign banks"

                        options={[
                          ...getStandingDataOptions(
                            standingData,
                            "sourceOfWealth",
                            ["kw", "gc"],
                            false
                          ),
                        ]}
                      />
                    </Form.Item>
                  </div>
                )}

              {form
                .getFieldValue("wealthInformation")
                ?.includes("sourceOfWealth2") && (
                  <div className="field flex column">
                    <Label text="Credit facilities from local / foreign banks" />
                    <Form.Item
                      name="sourceOfWealth2AmtId"
                      rules={[
                        {
                          required: true,
                          message:
                            "Please select Credit facilities from local / foreign banks!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Credit facilities from local / foreign banks"

                        options={[
                          ...getStandingDataOptions(
                            standingData,
                            "sourceOfWealth",
                            ["kw", "gc"],
                            false
                          ),
                        ]}
                      />
                    </Form.Item>
                  </div>
                )}

              {form
                .getFieldValue("wealthInformation")
                ?.includes("sourceOfWealth3") && (
                  <div className="field flex column">
                    <Label text="Inheritance" />
                    <Form.Item
                      name="sourceOfWealth3AmtId"
                      rules={[
                        {
                          required: true,
                          message: "Please select Inheritance!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Inheritance"

                        options={[
                          ...getStandingDataOptions(
                            standingData,
                            "sourceOfWealth",
                            ["kw", "gc"],
                            false
                          ),
                        ]}
                      />
                    </Form.Item>
                  </div>
                )}

              {form
                .getFieldValue("wealthInformation")
                ?.includes("sourceOfWealth4") && (
                  <div className="field flex column">
                    <Label text="Local / foreign real estate" />
                    <Form.Item
                      name="sourceOfWealth4AmtId"
                      rules={[
                        {
                          required: true,
                          message: "Please select Local / foreign real estate!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Local / foreign real estate"

                        options={[
                          ...getStandingDataOptions(
                            standingData,
                            "sourceOfWealth",
                            ["kw", "gc"],
                            false
                          ),
                        ]}
                      />
                    </Form.Item>
                  </div>
                )}

              {form
                .getFieldValue("wealthInformation")
                ?.includes("sourceOfWealth5") && (
                  <div className="field flex column">
                    <Label text="Investments in local / foreign real estate projects" />
                    <Form.Item
                      name="sourceOfWealth5AmtId"
                      rules={[
                        {
                          required: true,
                          message:
                            "Please select Investments in local / foreign real estate projects!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Investments in local / foreign real estate projects"

                        options={[
                          ...getStandingDataOptions(
                            standingData,
                            "sourceOfWealth",
                            ["kw", "gc"],
                            false
                          ),
                        ]}
                      />
                    </Form.Item>
                  </div>
                )}

              {form
                .getFieldValue("wealthInformation")
                ?.includes("sourceOfWealth6") && (
                  <div className="field flex column">
                    <Label text="Investments in local / foreign securities" />
                    <Form.Item
                      name="sourceOfWealth6AmtId"
                      rules={[
                        {
                          required: true,
                          message:
                            "Please select Investments in local / foreign securities!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Investments in local / foreign securities"

                        options={[
                          ...getStandingDataOptions(
                            standingData,
                            "sourceOfWealth",
                            ["kw", "gc"],
                            false
                          ),
                        ]}
                      />
                    </Form.Item>
                  </div>
                )}

              <div className="fields flex flex-col">
                <Label text="Additional Details on Source of Wealth" />
                <Form.Item name="sourceOfWealthDetails">
                  <TextArea
                    rows={4}
                    placeholder="Enter Additional Details on Source of Wealth"
                  />
                </Form.Item>
              </div>
            </div>
          </CollapseComponent>
          <div className="flex w-full flex-wrap items-center justify-end mt-7  pt-5">
            <div className="flex justify-between items-center w-[70%] md:w-[48%]">
              <button
                onClick={() =>
                  navigate(
                    `/home/business-account/${accountId}/${activeKyc}/details?tab=7${isApproveCase ? "&approve=1" : ""
                    }`
                  )
                }
                className="rounded-lg bg-white w-[48%] py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
              >
                Cancel
              </button>
              <button
                // disabled={!submitForm}
                className="disabled:opacity-50 disabled:cursor-not-allowed rounded-lg bg-blue-500 w-[48%] py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white"
              >
                Save
              </button>
            </div>
          </div>
        </Form>
      )}
    </div>
  );
};

export default EditFinancial;
